/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import {
    Component,
    Input,
    Output,
    EventEmitter,
    Injector,
    Optional,
    NgZone,
    forwardRef,
    OnDestroy,
    OnInit,
    DestroyRef,
    QueryList,
    ViewChildren,
    inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatDividerModule } from '@angular/material/divider';
import { QRCodeComponent } from 'angularx-qrcode';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { BackToCloseModalDirective } from '../../directives/back-to-close-modal.directive';
import { HyperLinkUserActivationGuardDirective } from '../../directives/hyperlink-user-activation-guard.directive';
import { NvHigBackgroundImageOverlayDirective } from '../../directives/nv-hig-background-image-overlay.directive';
import { OffDeviceLinkDirective } from '../../directives/off-device-link.directive';
import { SpatialNavigationContainerDirective } from '../../directives/spatial-navigation-container.directive';
import { HeadingFocusableDirective } from '../../directives/heading-focusable.directive';
import { TrapFocusDirective } from '../../directives/trap-focus.directive';
import { OffDeviceLinkService } from '../../services/off-device-link.service';
import {
    SnackbarTimeoutNotificationService,
    TimeoutWarningEvent,
    TimeoutWarningType
} from '../../services/timeout-notification.service';
import {
    NvGenericDialogConfig,
    NvGenericDialogData,
    NvHigThreeQuarterSheetRef,
    addPanelClass
} from '../nv-generic-dialog/nv-generic-dialog.types';
import { NvHigCellComparisonComponent } from '../nv-hig-cell-upgrade/nv-hig-cell_comparison.component';
import { NvHigListInsertComponent } from '../nv-hig-list-insert/nv-hig-list-insert.component';
import { NvHigListItemComponent } from '../nv-hig-list-item/nv-hig-list-item.component';

@Component({
    selector: 'nv-hig-three-quarter-sheet',
    templateUrl: './nv-hig-three-quarter-sheet.component.html',
    styleUrls: ['./nv-hig-three-quarter-sheet.component.scss'],
    providers: [SnackbarTimeoutNotificationService],
    imports: [
        FlexModule,
        SpatialNavigationContainerDirective,
        TrapFocusDirective,
        NvHigBackgroundImageOverlayDirective,
        MatProgressSpinnerModule,
        QRCodeComponent,
        forwardRef(() => OffDeviceLinkDirective),
        HyperLinkUserActivationGuardDirective,
        NgClass,
        NvHigCellComparisonComponent,
        NvHigListInsertComponent,
        NvHigListItemComponent,
        MatIconModule,
        MatButtonModule,
        AutoFocusDirective,
        HeadingFocusableDirective,
        NgTemplateOutlet,
        TranslateModule,
        MatDividerModule
    ]
})
export class NvHigThreeQuarterSheetComponent extends BackToCloseModalDirective implements OnInit, OnDestroy {
    @Output() primaryButtonClick = new EventEmitter<MouseEvent>();
    @Output() secondaryButtonClick = new EventEmitter<MouseEvent>();
    @Output() radioButtonClick = new EventEmitter();
    @Output() augmentedButtonClick = new EventEmitter<string>();
    @Output() linkClick = new EventEmitter();
    @Input() data: NvGenericDialogData = {};

    private expiredTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private readonly destroyRef = inject(DestroyRef);
    private timeoutNotificationService?: SnackbarTimeoutNotificationService;
    private componentInjector: Injector;

    /** All `nvAutoFocus` directives in the sheet — used by {@link focusAutoTarget}. */
    @ViewChildren(AutoFocusDirective) private autoFocusDirectives?: QueryList<AutoFocusDirective>;

    /**
     * Resolves which button type should receive auto-focus.
     *
     * Priority when `autoFocusTarget` is not explicitly set:
     *   1. radio          — radio buttons are the primary interactive element
     *   2. primary        — single primary action button
     *   3. augmented      — dynamic action buttons
     *
     * `autoFocusOnPrimaryButton: false` is treated as 'none' for backward compatibility.
     */
    get autoFocusTarget(): 'primary' | 'radio' | 'augmented' | 'none' {
        if (this.data.autoFocusTarget) {
            return this.data.autoFocusTarget;
        }
        if (this.data.autoFocusOnPrimaryButton === false) {
            return 'none';
        }
        if (this.data.radioButtonOptions) return 'radio';
        if (this.data.primaryButton) return 'primary';
        if (this.data.threeQuarterSheetData?.augmentedButtons?.length) return 'augmented';
        return 'none';
    }

    get shouldShowLinkRegion(): boolean {
        const link = this.data.link;
        return !!link && !!(link.url || link.linkClickedCallback);
    }

    static open(matDialog: MatDialog, config: NvGenericDialogConfig): NvHigThreeQuarterSheetRef {
        config.maxWidth = '100vw';
        addPanelClass(config, { threeQuarterSheet: true });
        return matDialog.open(NvHigThreeQuarterSheetComponent, { ...config, width: '100%', height: '100%' });
    }

    constructor(
        injector: Injector,
        private offDeviceLinkService: OffDeviceLinkService,
        @Optional() dialogRef: MatDialogRef<any>,
        @Optional() bottomSheetRef: MatBottomSheetRef<any>,
        ngZone: NgZone
    ) {
        super(dialogRef, bottomSheetRef, injector, ngZone);
        this.componentInjector = injector;
        this.data = injector.get(MAT_DIALOG_DATA, null);
    }

    ngOnInit(): void {
        const timeoutNotificationConfig = this.data?.threeQuarterSheetData?.timeoutNotificationConfig;
        if (timeoutNotificationConfig) {
            // Dynamically inject service only when timeout config is present
            this.timeoutNotificationService = this.componentInjector.get(SnackbarTimeoutNotificationService, null);

            if (this.timeoutNotificationService) {
                this.timeoutNotificationService.start(timeoutNotificationConfig);
                this.setupTimeoutNotificationSubscription();
            }
        }
    }

    private setupTimeoutNotificationSubscription(): void {
        if (!this.timeoutNotificationService) {
            return;
        }
        this.timeoutNotificationService.timeoutWarning$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: TimeoutWarningEvent) => {
                this.handleTimeoutWarning(event);
            });
    }

    private handleTimeoutWarning(event: TimeoutWarningEvent): void {
        // Snackbars are now shown by the service
        // Component only needs to handle expired event to close dialog
        if (event.type === TimeoutWarningType.Expired) {
            // Clear any existing expired timeout
            if (this.expiredTimeoutId) {
                clearTimeout(this.expiredTimeoutId);
            }
            const timeoutConfig = this.data?.threeQuarterSheetData?.timeoutNotificationConfig;
            const delayMs = timeoutConfig?.dialogCloseDelayMs ?? 2000; // Default: 2000ms
            // Delay allows snackbar to render and be announced by screen readers
            // Can be configured or set to 0 to close immediately
            if (delayMs > 0) {
                this.expiredTimeoutId = setTimeout(() => {
                    this.onPrimaryButtonClick();
                    this.expiredTimeoutId = null;
                }, delayMs);
            } else {
                // Close immediately if delay is 0
                this.onPrimaryButtonClick();
            }
        }
    }

    onPrimaryButtonClick(event?: MouseEvent) {
        this.primaryButtonClick.emit(event);
    }

    onSecondaryButtonClick(event?: MouseEvent) {
        // Stop timeout tracking (service will dismiss snackbars)
        this.timeoutNotificationService?.stop();
        this.secondaryButtonClick.emit(event);
    }

    onCheckboxClick() {
        this.data.checkboxState = true;
        const buttonClick = this.data.checkbox.primaryActionInThreeQuarterSheet
            ? this.primaryButtonClick
            : this.secondaryButtonClick;
        buttonClick.emit();
    }

    onRadioButtonClick(option: string) {
        this.data.radioOptionSelected = option;
        this.radioButtonClick.emit();
        this.primaryButtonClick.emit();
    }

    onLinkClick(url: string, shortUrl?: string, shortUrlDescription?: string, callback?: () => void) {
        if (!!url) {
            // Primary button for off-device link is already the close button, if we are coming from another
            // three quarter sheet which has a back button, do not persist that back button in the sheet data
            const offDeviceThreeQuarterSheetData = this.data ? { ...this.data.threeQuarterSheetData } : undefined;
            if (offDeviceThreeQuarterSheetData?.augmentedButtons) {
                offDeviceThreeQuarterSheetData.augmentedButtons =
                    offDeviceThreeQuarterSheetData.augmentedButtons.filter(button => button.id !== 'Back');
            }
            this.offDeviceLinkService.open(url, {
                shortUrl,
                shortUrlDescription,
                threeQuarterSheetData: offDeviceThreeQuarterSheetData,
                preferBottomSheet: true
            });
        } else if (!!callback) {
            callback();
        }
        this.linkClick.emit();
    }

    onAugmentedButtonClick(id: string) {
        this.augmentedButtonClick.emit(id);
    }

    /** Re-applies auto-focus to the resolved target. Call after reassigning `data`. */
    public focusAutoTarget(): void {
        setTimeout(() => {
            const target = this.autoFocusDirectives?.find(directive => directive.focusEnabled);
            target?.refocus();
        });
    }

    ngOnDestroy(): void {
        // Clear expired timeout if component is destroyed before it fires
        if (this.expiredTimeoutId) {
            clearTimeout(this.expiredTimeoutId);
            this.expiredTimeoutId = null;
        }
        this.timeoutNotificationService?.stop();
        this.timeoutNotificationService?.destroy();
        super.ngOnDestroy();
    }
}
